import React, { useState, useEffect } from 'react'
import { Calendar, Plus, Edit2, Trash2, Save, X, Globe, Star, RefreshCw } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, parseISO } from 'date-fns'

interface Holiday {
  id: number
  name: string
  date: string
  type: 'federal' | 'school' | 'religious' | 'custom'
  is_is_recurring: boolean
  schools_closed: boolean
  no_chores: boolean
  notes?: string
  created_at?: string
}

export const HolidaySchedule: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [filterType, setFilterType] = useState<string>('all')
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const [newHoliday, setNewHoliday] = useState<Partial<Holiday>>({
    name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    type: 'custom',
    is_is_recurring: false,
    schools_closed: false,
    no_chores: false,
    notes: ''
  })

  useEffect(() => {
    loadHolidays()
  }, [selectedYear])

  const loadHolidays = async () => {
    setLoading(true)
    try {
      const data = await apiClient.get('/api/admin/holidays')
      // Ensure data is an array
      const holidaysList = Array.isArray(data) ? data : (data?.holidays || [])
      setHolidays(holidaysList)
    } catch (error) {
      console.error('Failed to load holidays:', error)
      setHolidays([])
    } finally {
      setLoading(false)
    }
  }

  const handleImportDefault = async () => {
    try {
      await apiClient.post('/api/school-calendar/import-default-holidays', {
        year: selectedYear
      })
      toast.success('Default holidays imported successfully')
      loadHolidays()
    } catch (error) {
      toast.error('Failed to import default holidays')
    }
  }

  const handleSave = async () => {
    if (!newHoliday.name || !newHoliday.date) {
      toast.error('Please provide holiday name and date')
      return
    }

    try {
      if (editingId) {
        await apiClient.put(`/api/admin/holidays/${editingId}`, newHoliday)
        toast.success('Holiday updated successfully')
      } else {
        await apiClient.post('/api/admin/holidays', newHoliday)
        toast.success('Holiday added successfully')
      }

      setShowAddModal(false)
      setEditingId(null)
      setNewHoliday({
        name: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        type: 'custom',
        is_is_recurring: false,
        schools_closed: false,
        no_chores: false,
        notes: '',
        color: '#EF4444'
      })
      loadHolidays()
    } catch (error) {
      toast.error('Failed to save holiday')
    }
  }

  const handleEdit = (holiday: Holiday) => {
    setNewHoliday(holiday)
    setEditingId(holiday.id)
    setShowAddModal(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return

    try {
      await apiClient.delete(`/api/admin/holidays/${id}`)
      toast.success('Holiday deleted successfully')
      loadHolidays()
    } catch (error) {
      toast.error('Failed to delete holiday')
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'federal':
        return <Globe className="w-4 h-4 text-blue-500" />
      case 'school':
        return <Calendar className="w-4 h-4 text-green-500" />
      case 'religious':
        return <Star className="w-4 h-4 text-purple-500" />
      default:
        return <Calendar className="w-4 h-4 text-gray-500" />
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'federal':
        return 'bg-blue-100 text-blue-800'
      case 'school':
        return 'bg-green-100 text-green-800'
      case 'religious':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const filteredHolidays = filterType === 'all'
    ? (Array.isArray(holidays) ? holidays : [])
    : (Array.isArray(holidays) ? holidays.filter(h => h.type === filterType) : [])

  const upcomingHolidays = filteredHolidays
    .filter(h => new Date(h.date + 'T12:00:00') >= new Date())
    .sort((a, b) => new Date(a.date + 'T12:00:00').getTime() - new Date(b.date + 'T12:00:00').getTime())
    .slice(0, 5)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Holiday Schedule</h2>
          <p className="text-gray-600">Manage holidays and special dates</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleImportDefault}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Import Defaults
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Holiday
          </button>
        </div>
      </div>

      {/* Year Selector and Filter */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border rounded-lg"
            >
              <option value={selectedYear - 1}>{selectedYear - 1}</option>
              <option value={selectedYear}>{selectedYear}</option>
              <option value={selectedYear + 1}>{selectedYear + 1}</option>
            </select>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1 rounded-lg ${
                  filterType === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilterType('federal')}
                className={`px-3 py-1 rounded-lg ${
                  filterType === 'federal' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                Federal
              </button>
              <button
                onClick={() => setFilterType('school')}
                className={`px-3 py-1 rounded-lg ${
                  filterType === 'school' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                School
              </button>
              <button
                onClick={() => setFilterType('religious')}
                className={`px-3 py-1 rounded-lg ${
                  filterType === 'religious' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                Religious
              </button>
              <button
                onClick={() => setFilterType('custom')}
                className={`px-3 py-1 rounded-lg ${
                  filterType === 'custom' ? 'bg-blue-500 text-white' : 'bg-gray-100'
                }`}
              >
                Custom
              </button>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {filteredHolidays.length} holidays
          </div>
        </div>
      </div>

      {/* Upcoming Holidays */}
      {upcomingHolidays.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-900 mb-3">Upcoming Holidays</h3>
          <div className="space-y-2">
            {upcomingHolidays.map(holiday => (
              <div key={holiday.id} className="flex items-center justify-between">
                <div className="flex items-center">
                  {getTypeIcon(holiday.type)}
                  <span className="ml-2 font-medium">{holiday.name}</span>
                </div>
                <div className="text-sm text-gray-600">
                  {format(parseISO(holiday.date), 'MMM d, yyyy')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Holidays Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">{selectedYear} Holidays</h3>
        </div>

        {filteredHolidays.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No holidays scheduled</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holiday
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schools Closed
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No Chores
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recurring
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredHolidays.map(holiday => (
                  <tr key={holiday.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {format(parseISO(holiday.date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{holiday.name}</div>
                        {holiday.notes && (
                          <div className="text-xs text-gray-500">{holiday.notes}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(holiday.type)}`}>
                        {holiday.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {holiday.schools_closed && (
                        <span className="text-green-600">✓</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {holiday.no_chores && (
                        <span className="text-green-600">✓</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {holiday.is_recurring && (
                        <span className="text-blue-600">↻</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handleEdit(holiday)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(holiday.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">
              {editingId ? 'Edit Holiday' : 'Add Holiday'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Holiday Name</label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Thanksgiving"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={newHoliday.type}
                  onChange={(e) => setNewHoliday({ ...newHoliday, type: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="federal">Federal Holiday</option>
                  <option value="school">School Holiday</option>
                  <option value="religious">Religious Holiday</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newHoliday.notes}
                  onChange={(e) => setNewHoliday({ ...newHoliday, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newHoliday.schools_closed}
                    onChange={(e) => setNewHoliday({ ...newHoliday, schools_closed: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Schools closed</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newHoliday.no_chores}
                    onChange={(e) => setNewHoliday({ ...newHoliday, no_chores: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">No chores on this day</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newHoliday.recurring}
                    onChange={(e) => setNewHoliday({ ...newHoliday, is_recurring: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Recurring annually</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setEditingId(null)
                  setNewHoliday({
                    name: '',
                    date: format(new Date(), 'yyyy-MM-dd'),
                    type: 'custom',
                    is_recurring: false,
                    schools_closed: false,
                    no_chores: false,
                    notes: '',
                    color: '#EF4444'
                  })
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4 inline mr-2" />
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}